import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, EMPTY, filter, switchMap, take, tap } from 'rxjs';
import { Button } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { APP_PATHS } from '../../../../../../../core/config/app-paths';
import { PreviewDataType } from '../../../../../../../shared/enums/preview-data-type.enum';
import { TagSeverity } from '../../../../../../../shared/enums/tag-severity.enum';
import {
  AdminPanelUserActionRequest,
  AdminPanelUserDetails,
  AdminPanelUserSecurityData,
} from '../../../../../../../shared/models/admin-panel-user-details';
import { AdminPanelUsersApi } from '../../../../../../../shared/services/api/admin-panel-users.api';
import { AdminPanelUserActionDialogComponent } from '../admin-panel-user-action-dialog/admin-panel-user-action-dialog.component';
import { AdminPanelUserDataSectionComponent } from '../admin-panel-user-data-section/admin-panel-user-data-section.component';
import { AdminPanelUserLoginsTableComponent } from '../admin-panel-user-logins-table/admin-panel-user-logins-table.component';
import { AdminPanelUserOrganizationsTableComponent } from '../admin-panel-user-organizations-table/admin-panel-user-organizations-table.component';
import { AdminPanelUserDataSectionField } from '../../../../../../../shared/models/admin-panel-user-data-section-field';
import { AdminPanelUserPreviewStateService } from '../../../../state/admin-panel-user-preview-state.service';
import { HttpResponseService } from '../../../../../../../core/services/http-error-response.service';
import { ConstLabelReaderPipe } from '../../../../../../../shared/pipes/const-label-reader.pipe';
import { PLEC_LABELS } from '../../../../../../../shared/enums/plec.enum';
import { RODZAJ_DOKUMENTU_LABELS } from '../../../../../../../shared/enums/rodzaj-dokumentu.enum';
import { STAN_CYWILNY_LABELS } from '../../../../../../../shared/enums/stan-cywilny.enum';
import { getBooleanLabel } from '../../../../../../../shared/utils/boolean.utils';

@Component({
  selector: 'app-admin-panel-user-details',
  imports: [
    AdminPanelUserDataSectionComponent,
    AdminPanelUserLoginsTableComponent,
    AdminPanelUserOrganizationsTableComponent,
    Button,
  ],
  providers: [ConstLabelReaderPipe],
  templateUrl: './admin-panel-user-details.component.html',
  styleUrl: './admin-panel-user-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPanelUserDetailsComponent {
  readonly #destroyRef = inject(DestroyRef);
  readonly #adminPanelUsersApi = inject(AdminPanelUsersApi);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #dialogService = inject(DialogService);
  readonly #router = inject(Router);
  readonly #userPreviewState = inject(AdminPanelUserPreviewStateService);
  readonly #constLabelPipe = inject(ConstLabelReaderPipe);

  userId = input.required<number>();
  userDetailsInput = input.required<AdminPanelUserDetails>();
  protected readonly userDetails = signal<AdminPanelUserDetails | null>(null);

  protected readonly GENDER_LABELS = PLEC_LABELS;
  protected readonly DOCUMENT_TYPE_LABELS = RODZAJ_DOKUMENTU_LABELS;
  protected readonly MARTIAL_STATUS_LABELS = STAN_CYWILNY_LABELS;

  constructor() {
    effect(() => {
      this.userDetails.set(this.userDetailsInput());
    });
  }

  protected readonly personalSectionFields = computed<
    AdminPanelUserDataSectionField[]
  >(() => {
    const user = this.userDetails();

    if (!user) {
      return [];
    }

    const userGenderTransformed = this.#constLabelPipe.transform(
      user.personalData.gender,
      PLEC_LABELS,
    );

    const userMartialStatusTransformed = this.#constLabelPipe.transform(
      user.personalData.maritalStatus,
      STAN_CYWILNY_LABELS,
    );

    const userDocumentTypeTransformed = this.#constLabelPipe.transform(
      user.personalData.identityDocumentType,
      RODZAJ_DOKUMENTU_LABELS,
    );

    return [
      { label: 'Imię', value: user.personalData.firstName },
      {
        label: 'Drugie imię',
        value: user.personalData.middleName,
      },
      { label: 'Nazwisko', value: user.personalData.lastName },
      { label: 'PESEL', value: user.personalData.pesel },
      { label: 'Obywatelstwo', value: user.personalData.citizenship },
      {
        label: 'Data urodzenia',
        value: user.personalData.birthDate,
        dataType: PreviewDataType.DATE,
      },
      { label: 'Płeć', value: userGenderTransformed ?? '' },
      { label: 'Stan cywilny', value: userMartialStatusTransformed ?? '' },
      {
        label: 'Rodzaj dokumentu',
        value: userDocumentTypeTransformed ?? '',
        itemClass: 'md:col-start-1 md:row-start-4',
      },
      {
        label: 'Seria i numer dokumentu tożsamości',
        value: user.personalData.identityDocumentNumber,
        itemClass: 'md:col-start-2 md:row-start-4',
      },
    ];
  });

  protected readonly contactSectionFields = computed<
    AdminPanelUserDataSectionField[]
  >(() => {
    const user = this.userDetails();

    if (!user) {
      return [];
    }

    return [
      { label: 'Adres e-mail', value: user.contactData.email },
      {
        label: 'Status adresu e-mail',
        value: user.contactData.emailVerified
          ? 'Zweryfikowany'
          : 'Niezweryfikowany',
        dataType: PreviewDataType.STATUS,
        statusSeverity: user.contactData.emailVerified
          ? TagSeverity.SUCCESS
          : TagSeverity.DANGER,
      },
      {
        label: 'Data ostatniego wysłania linka weryfikacyjnego',
        value: user.contactData.verificationLinkLastSentAt,
        dataType: PreviewDataType.DATE_TIME,
      },
      {
        label: 'Telefon komórkowy',
        value: user.contactData.mobilePhone,
        itemClass: 'md:col-start-1 md:row-start-2',
      },
      {
        label: 'Adres zamieszkania',
        value: this.#formatAddressForPreview(
          user.contactData.residentialAddress,
        ),
        itemClass: 'md:col-start-1 md:row-start-3',
      },
      {
        label: 'Adres korespondencyjny',
        value: this.#formatAddressForPreview(
          user.contactData.correspondenceAddress,
        ),
        itemClass: 'md:col-start-2 md:row-start-3',
      },
    ];
  });

  protected readonly securitySectionFields = computed<
    AdminPanelUserDataSectionField[]
  >(() => {
    const user = this.userDetails();

    if (!user) {
      return [];
    }

    return [
      {
        label: 'Status konta',
        value: this.#getAccountStatusLabel(user.securityData),
        dataType: PreviewDataType.STATUS,
        statusSeverity: this.#getAccountStatusSeverity(user.securityData),
      },
      {
        label: 'Data zablokowania/dezaktywacji konta',
        value: user.securityData.blockedAt,
        dataType: PreviewDataType.DATE_TIME,
        labelTooltip:
          'W przypadku “-” konto użytkownika zostało aktywowane.',
      },
      {
        label: 'Data akceptacji regulaminu',
        value: user.securityData.termsAcceptedAt,
        dataType: PreviewDataType.DATE_TIME,
        itemClass: 'md:col-start-1 md:row-start-2',
      },
      {
        label: 'Uprawnienie do administrowania systemem',
        value: getBooleanLabel(user.securityData.hasSystemAdminRole),
        itemClass: 'md:col-start-2 md:row-start-2',
      },
      {
        label: 'Data nadania uprawnienia do administrowania systemem',
        value: user.securityData.systemAdminGrantedAt,
        dataType: PreviewDataType.DATE_TIME,
        labelTooltip:
          'W przypadku “-” użytkownikowi zostało odebrane prawo do administrowania systemem.',
        itemClass: 'md:col-start-3 md:row-start-2',
      },
    ];
  });

  protected openAccountLockDialog(): void {
    const user = this.userDetails();

    if (!user) {
      return;
    }

    const shouldLock = !user.securityData.blocked;
    const header = shouldLock
      ? 'Zablokuj konto użytkownika'
      : 'Odblokuj konto użytkownika';
    const verb = shouldLock ? 'zablokowania' : 'odblokowania';

    const ref = this.#dialogService.open(AdminPanelUserActionDialogComponent, {
      header,
      closable: true,
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      width: '700px',
      breakpoints: {
        '960px': '90vw',
        '640px': '95vw',
      },
      data: {
        description: `Czy potwierdzasz zamiar ${verb} konta użytkownika?`,
        confirmLabel: 'Potwierdź',
      },
    });

    if (!ref) {
      return;
    }

    ref.onClose
      .pipe(
        take(1),
        filter((justification): justification is string =>
          Boolean(justification),
        ),
        switchMap((justification) =>
          this.#adminPanelUsersApi.toggleUserAccountLock$(
            user.id,
            this.#createActionRequest(justification),
          ),
        ),
        tap((userDetails) => {
          this.#httpResponseService.showSuccessMessage(
            shouldLock
              ? 'Poprawnie zablokowano konto użytkownika.'
              : 'Poprawnie odblokowano konto użytkownika.',
          );
          this.userDetails.set(userDetails);
        }),
        catchError((error) => {
          this.#httpResponseService.showErrorMessage(
            error,
            shouldLock
              ? 'Nie udało się zablokować konta użytkownika.'
              : 'Nie udało się odblokować konta użytkownika.',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected openSystemAdminRoleDialog(): void {
    const user = this.userDetails();

    if (!user) {
      return;
    }

    const isCurrentlyAdminGranted = user.securityData.hasSystemAdminRole;
    const header = isCurrentlyAdminGranted
      ? 'Odbierz uprawnienia do administrowania systemem'
      : 'Nadaj uprawnienia do administrowania systemem';
    const verb = isCurrentlyAdminGranted ? 'odebrania' : 'udostępnienia';

    const ref = this.#dialogService.open(AdminPanelUserActionDialogComponent, {
      header,
      closable: true,
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      width: '700px',
      breakpoints: {
        '960px': '90vw',
        '640px': '95vw',
      },
      data: {
        description: `Czy potwierdzasz zamiar ${verb} użytkownikowi ${user.fullName} uprawnień do zarządzania formularzami wniosków, kontami użytkowników, komunikatami, regulaminem oraz raportami?`,
        confirmLabel: 'Potwierdź',
      },
    });

    if (!ref) {
      return;
    }

    ref.onClose
      .pipe(
        take(1),
        filter((justification): justification is string =>
          Boolean(justification),
        ),
        switchMap((justification) =>
          this.#adminPanelUsersApi.toggleUserSystemAdminRole$(
            user.id,
            this.#createActionRequest(justification),
          ),
        ),
        tap((userDetails) => {
          this.#httpResponseService.showSuccessMessage(
            isCurrentlyAdminGranted
              ? 'Poprawnie odebrano uprawnienia administratora.'
              : 'Poprawnie nadano uprawnienia administratora.',
          );
          this.userDetails.set(userDetails);
        }),
        catchError((error) => {
          this.#httpResponseService.showErrorMessage(
            error,
            isCurrentlyAdminGranted
              ? 'Nie udało się odebrać uprawnień administratora.'
              : 'Nie udało się nadać uprawnień administratora.',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected goBack(): void {
    this.#userPreviewState.clear();
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.users}`,
    ]);
  }

  protected get accountActionLabel(): string {
    return this.userDetails()?.securityData.blocked
      ? 'Odblokuj konto użytkownika'
      : 'Zablokuj konto użytkownika';
  }

  protected get adminRoleActionLabel(): string {
    return this.userDetails()?.securityData.hasSystemAdminRole
      ? 'Odbierz uprawnienie do administrowania systemem'
      : 'Nadaj uprawnienie do administrowania systemem';
  }

  #formatAddressForPreview(address: string | null): string | null {
    return (
      address
        ?.replace(/\bul\.\s+/gi, 'ul.\u00a0')
        .replace(/\bal\.\s+/gi, 'al.\u00a0') ?? null
    );
  }

  #createActionRequest(justification: string): AdminPanelUserActionRequest {
    return {
      justification,
    };
  }

  #getAccountStatusLabel(securityData: AdminPanelUserSecurityData): string {
    if (securityData.blocked) {
      return 'Zablokowane';
    }

    if (securityData.active) {
      return 'Aktywne';
    }

    return 'Dezaktywowane przez użytkownika';
  }

  #getAccountStatusSeverity(
    securityData: AdminPanelUserSecurityData,
  ): TagSeverity {
    return securityData.active && !securityData.blocked
      ? TagSeverity.SUCCESS
      : TagSeverity.DANGER;
  }
}
